import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { api } from '../api';
import type { AdminDashboardData, DashboardActivityPoint, DashboardEventSummary, DashboardOnboardedUser, UserActivityDetail } from '../types';
import type { ValidationHealthData } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import '../styles/admin-dashboard.css';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import type { RectangleProps } from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';

const ONBOARDED_USERS_PER_PAGE = 12;

type OverviewGroupConfig = {
  id: string;
  label: string;
  lifetimeDefs: Array<{ label: string; getValue: (totals: AdminDashboardData['totals']) => number }>;
  recentDefs: Array<{ label: string; getValue: (totals: AdminDashboardData['totals']) => number }>;
};

const VISUAL_OVERVIEW_GROUPS: OverviewGroupConfig[] = [
  {
    id: 'core',
    label: 'Users & Activity',
    lifetimeDefs: [
      { label: 'Users', getValue: (totals) => totals.totalUsers },
      { label: 'Entries', getValue: (totals) => totals.totalFlows },
      { label: 'Events', getValue: (totals) => totals.totalEvents },
      { label: 'Logins', getValue: (totals) => totals.totalLogins }
    ],
    recentDefs: [
      { label: 'Logins (7d)', getValue: (totals) => totals.loginsLast7Days },
      { label: 'Downloads (7d)', getValue: (totals) => totals.downloadsLast7Days },
      { label: 'Copies (7d)', getValue: (totals) => totals.copiesLast7Days }
    ]
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    lifetimeDefs: [
      { label: 'Downloads', getValue: (totals) => totals.totalDownloads },
      { label: 'Uploads', getValue: (totals) => totals.totalUploads },
      { label: 'Copies', getValue: (totals) => totals.totalCopies },
      { label: 'Entry Updates', getValue: (totals) => totals.totalEntryUpdates }
    ],
    recentDefs: [
      { label: 'Downloads (7d)', getValue: (totals) => totals.downloadsLast7Days },
      { label: 'Uploads (7d)', getValue: (totals) => totals.uploadsLast7Days },
      { label: 'Copies (7d)', getValue: (totals) => totals.copiesLast7Days },
      { label: 'Updates (7d)', getValue: (totals) => totals.entryUpdatesLast7Days }
    ]
  },
  {
    id: 'community',
    label: 'Forum & Requests',
    lifetimeDefs: [
      { label: 'Forum Threads', getValue: (totals) => totals.totalForumThreads },
      { label: 'Threads Answered', getValue: (totals) => totals.totalForumAnsweredThreads },
      { label: 'Requests (Open)', getValue: (totals) => totals.totalRequestsOpen },
      { label: 'Requests (In Dev)', getValue: (totals) => totals.totalRequestsInDevelopment },
      { label: 'Requests (Done)', getValue: (totals) => totals.totalRequestsDone }
    ],
    recentDefs: [
      { label: 'Threads (7d)', getValue: (totals) => totals.forumThreadsLast7Days },
      { label: 'Threads Answered (7d)', getValue: (totals) => totals.forumThreadsAnsweredLast7Days },
      { label: 'Requests (7d)', getValue: (totals) => totals.requestsCreatedLast7Days },
      { label: 'Reqs → Dev (7d)', getValue: (totals) => totals.requestsInDevelopmentLast7Days },
      { label: 'Reqs Done (7d)', getValue: (totals) => totals.requestsDoneLast7Days }
    ]
  }
];

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value ?? 0);
}

function formatTimestamp(value: string | null) {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function formatActivityDate(value: DashboardActivityPoint['date']) {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString();
}

function formatEventType(value: string) {
  return value
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function formatMetadata(metadata: Record<string, unknown> | null | undefined) {
  if (!metadata || Object.keys(metadata).length === 0) {
    return '—';
  }
  try {
    return JSON.stringify(metadata);
  } catch {
    return '—';
  }
}

function formatCsvValue(value: string | number | null | undefined) {
  const normalized = value ?? '';
  const stringValue = typeof normalized === 'string' ? normalized : String(normalized);
  const escaped = stringValue.replace(/"/g, '""');
  return `"${escaped}"`;
}

function formatCsvTimestamp(value: string | null) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString();
}

function safeValue(value: number | null | undefined) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
}

type LineColorMap = Record<string, string>;

const ACTIVITY_COLORS: LineColorMap = {
  logins: '#4a90e2',
  downloads: '#50e3c2',
  copies: '#f5a623',
  uploads: '#d0021b',
  updates: '#b388ff'
};

const ACTIVITY_SERIES_ORDER = ['logins', 'downloads', 'copies', 'uploads', 'updates'] as const;

interface ActivityTooltipEntry {
  name?: string | number;
  value?: ValueType;
  dataKey?: string | number;
  color?: string;
  stroke?: string;
}

type ActivityLegendEntry = {
  value?: string | number;
  dataKey?: string | number | ((...args: unknown[]) => unknown);
  color?: string;
};

function resolveDataKey(value: string | number | ((...args: unknown[]) => unknown) | undefined) {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return null;
}

function formatRole(role: string | null | undefined) {
  if (!role) {
    return '—';
  }
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function getInitials(name: string) {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return '?';
  }
  const parts = trimmed.split(/\s+/);
  const initials = parts
    .slice(0, 2)
    .map((part) => (part[0] ? part[0].toUpperCase() : ''))
    .join('');
  return initials || '?';
}

function ActivityTooltip({
  active,
  payload,
  label,
  styles
}: {
  active?: boolean;
  payload?: ReadonlyArray<ActivityTooltipEntry>;
  label?: string | number;
  styles: {
    contentStyle: CSSProperties;
    labelStyle: CSSProperties;
    itemStyle?: CSSProperties;
  };
}) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div className="admin-dashboard__tooltip" style={styles.contentStyle}>
      <div className="admin-dashboard__tooltip-label" style={styles.labelStyle}>
        {label}
      </div>
      <ul className="admin-dashboard__tooltip-list">
        {ACTIVITY_SERIES_ORDER.map((seriesKey) => {
          const entry = payload.find((item) => resolveDataKey(item.dataKey) === seriesKey);
          if (!entry) {
            return null;
          }
          const resolvedColor =
            entry.color ||
            entry.stroke ||
            ACTIVITY_COLORS[seriesKey] ||
            styles.itemStyle?.color ||
            styles.labelStyle.color ||
            '#fff';
          return (
            <li key={seriesKey} style={{ color: resolvedColor }}>
              {entry.name}: {entry.value}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ActivityLegend({ payload }: { payload?: ReadonlyArray<ActivityLegendEntry> }) {
  if (!payload || payload.length === 0) {
    return null;
  }
  const entriesByKey = new Map<string, ActivityLegendEntry>();
  payload.forEach((entry) => {
    const key = resolveDataKey(entry.dataKey);
    if (key) {
      entriesByKey.set(key, entry);
    }
  });
  return (
    <ul className="admin-dashboard__activity-legend">
      {ACTIVITY_SERIES_ORDER.map((seriesKey) => {
        const entry = entriesByKey.get(seriesKey);
        if (!entry) {
          return null;
        }
        const color = entry.color || ACTIVITY_COLORS[seriesKey] || '#fff';
        const label = entry.value ?? seriesKey;
        return (
          <li key={seriesKey} style={{ color }}>
            <span className="admin-dashboard__legend-dot" style={{ backgroundColor: color }} />
            {label}
          </li>
        );
      })}
    </ul>
  );
}

interface VisualOverviewBarProps extends RectangleProps {
  payload?: { name?: string };
  fill?: string;
  activeName: string | null;
  onHover?: (name: string | null) => void;
  onBlur?: () => void;
}

function VisualOverviewBar({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  fill = '#4a90e2',
  payload,
  activeName,
  onHover,
  onBlur
}: VisualOverviewBarProps) {
  const hasFocus = Boolean(activeName);
  const isActive = hasFocus && payload?.name === activeName;
  const shouldDim = hasFocus && !isActive;
  const inactiveFill = fill.length === 7 ? `${fill}33` : fill;
  const barColor = shouldDim ? inactiveFill : fill;
  const columnHeight = y + height;
  const overlayWidth = Math.max(width, 16);
  const overlayX = x + width / 2 - overlayWidth / 2;

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={barColor} rx={8} ry={8} />
      <rect
        x={overlayX}
        y={0}
        width={overlayWidth}
        height={Math.max(columnHeight, 0)}
        fill="transparent"
        pointerEvents="all"
        onMouseEnter={() => onHover?.(payload?.name ?? null)}
        onMouseLeave={() => onBlur?.()}
        onMouseMove={() => onHover?.(payload?.name ?? null)}
        onTouchStart={() => onHover?.(payload?.name ?? null)}
        onTouchEnd={() => onBlur?.()}
      />
    </g>
  );
}

export function AdminDashboardPage() {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardedPage, setOnboardedPage] = useState(1);
  const [overviewMode, setOverviewMode] = useState<'lifetime' | 'recent'>('lifetime');
  const [overviewGroup, setOverviewGroup] = useState<string>(VISUAL_OVERVIEW_GROUPS[0].id);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<UserActivityDetail | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [onboardedSearch, setOnboardedSearch] = useState('');
  const [userActivitySearch, setUserActivitySearch] = useState('');
  const [deletedFlows, setDeletedFlows] = useState<Array<{
    id: string;
    title: string;
    kind: string;
    status: string;
    deletedAt: string;
    deletedBy: string;
    deletedByName: string;
    daysRemaining: number;
  }>>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayEvents, setDayEvents] = useState<DashboardEventSummary[]>([]);
  const [dayEventsLoading, setDayEventsLoading] = useState(false);
  const [healthData, setHealthData] = useState<ValidationHealthData | null>(null);

  const handleBarHover = useCallback((name: string | null) => {
    setHoveredBar((prev) => {
      if (prev === name) {
        return prev;
      }
      return name;
    });
  }, []);
  const { user } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    let isActive = true;

    if (!user) {
      return () => {
        isActive = false;
      };
    }

    if (user.role !== 'admin') {
      setData(null);
      setError('You need admin permissions to view this dashboard.');
      setLoading(false);
      return () => {
        isActive = false;
      };
    }

    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const [response, health] = await Promise.all([
          api.adminDashboard(),
          api.getValidationHealth().catch(() => null)
        ]);
        if (!isActive) {
          return;
        }
        setData(response);
        if (health) setHealthData(health);
      } catch (err) {
        if (isActive) {
          setError((err as Error).message || 'Unable to load dashboard.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      isActive = false;
    };
  }, [user]);

  const fetchDeletedFlows = useCallback(async () => {
    setDeletedLoading(true);
    try {
      const result = await api.deletedFlows();
      setDeletedFlows(result);
    } catch {
      setDeletedFlows([]);
    } finally {
      setDeletedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      void fetchDeletedFlows();
    }
  }, [user?.role, fetchDeletedFlows]);

  const handleRestore = useCallback(async (id: string) => {
    if (!window.confirm('Restore this entry? It will reappear in the marketplace.')) return;
    setRestoringId(id);
    try {
      await api.restoreFlow(id);
      setDeletedFlows((current) => current.filter((f) => f.id !== id));
    } catch (err) {
      alert((err as Error).message || 'Failed to restore entry.');
    } finally {
      setRestoringId(null);
    }
  }, []);

  const handleDayClick = useCallback(async (date: string) => {
    if (selectedDay === date) {
      setSelectedDay(null);
      setDayEvents([]);
      return;
    }
    setSelectedDay(date);
    setDayEventsLoading(true);
    try {
      const events = await api.adminDayActivity(date);
      setDayEvents(events);
    } catch {
      setDayEvents([]);
    } finally {
      setDayEventsLoading(false);
    }
  }, [selectedDay]);

  const openUserDetail = useCallback(async (userId: string) => {
    setSelectedUserId(userId);
    setUserDetail(null);
    setUserDetailLoading(true);
    try {
      const detail = await api.adminUserActivity(userId);
      setUserDetail(detail);
    } catch {
      setUserDetail(null);
    } finally {
      setUserDetailLoading(false);
    }
  }, []);

  const filteredUserActivity = useMemo(() => {
    const all = data?.userActivity ?? [];
    const q = userActivitySearch.trim().toLowerCase();
    if (!q) {
      return all;
    }
    return all.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.email && u.email.toLowerCase().includes(q))
    );
  }, [data, userActivitySearch]);

  const closeUserDetail = useCallback(() => {
    setSelectedUserId(null);
    setUserDetail(null);
  }, []);

  const chartTheme = useMemo(
    () =>
      theme === 'dark'
        ? {
            axisTick: 'rgba(229, 233, 240, 0.85)',
            axisLine: 'rgba(229, 233, 240, 0.35)',
            grid: 'rgba(229, 233, 240, 0.18)',
            legend: 'rgba(229, 233, 240, 0.75)',
            tooltipBg: '#0f1b34',
            tooltipBorder: 'rgba(255, 255, 255, 0.2)',
            tooltipText: '#f5f7ff',
            tooltipLabel: 'rgba(229, 233, 240, 0.75)',
            cursorFill: 'rgba(255, 255, 255, 0.1)',
            barFill: '#8da2ff'
          }
        : {
            axisTick: 'rgba(27, 36, 48, 0.75)',
            axisLine: 'rgba(27, 36, 48, 0.25)',
            grid: 'rgba(27, 36, 48, 0.08)',
            legend: 'rgba(27, 36, 48, 0.8)',
            tooltipBg: '#ffffff',
            tooltipBorder: 'rgba(27, 36, 48, 0.12)',
            tooltipText: '#1b2430',
            tooltipLabel: 'rgba(27, 36, 48, 0.65)',
            cursorFill: 'rgba(27, 36, 48, 0.06)',
            barFill: '#4a90e2'
          },
    [theme]
  );

const chartTooltipStyles = useMemo(
    () => ({
      contentStyle: {
        background: chartTheme.tooltipBg,
        border: `1px solid ${chartTheme.tooltipBorder}`,
        borderRadius: '12px',
        boxShadow: '0 12px 30px rgba(3, 10, 30, 0.4)',
        color: chartTheme.tooltipText
      },
      labelStyle: {
        color: chartTheme.tooltipLabel,
        fontWeight: 600
      },
      itemStyle: {
        color: chartTheme.tooltipText
      }
    }),
    [chartTheme]
  );

  const activityHasSignal = useMemo(
    () =>
      data?.activityTrend.some(
        (point) => point.logins || point.downloads || point.copies || point.uploads || point.updates
      ) ?? false,
    [data]
  );

  const handleChartClick = useCallback(
    (chartData: { activeLabel?: string } | null) => {
      if (chartData?.activeLabel) {
        void handleDayClick(chartData.activeLabel);
      }
    },
    [handleDayClick]
  );

  const selectedDayLabel = selectedDay ? formatActivityDate(selectedDay) : null;

  const renderDot = useCallback(
    (lineColor: string) =>
      (props: { cx?: number; cy?: number; payload?: { date?: string } }) => {
        const { cx = 0, cy = 0, payload } = props;
        const isSelected = selectedDay != null && payload?.date === selectedDay;
        if (!isSelected) return <circle cx={cx} cy={cy} r={0} />;
        return (
          <circle
            cx={cx}
            cy={cy}
            r={7}
            fill={lineColor}
            stroke="#fff"
            strokeWidth={2}
            style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.3))' }}
          />
        );
      },
    [selectedDay]
  );

  const activityChart = useMemo(() => {
    if (!data) {
      return null;
    }
    return (
      <section className="admin-dashboard__section">
        <div className="admin-dashboard__section-header">
          <h2>14-Day Activity</h2>
          {selectedDay && (
            <div className="admin-dashboard__section-tools">
              <button
                type="button"
                className="admin-dashboard__button admin-dashboard__button--ghost"
                onClick={() => { setSelectedDay(null); setDayEvents([]); }}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
        {activityHasSignal ? (
          <div className="admin-dashboard__activity-chart-wrapper">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={data.activityTrend}
                onClick={handleChartClick}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatActivityDate}
                  tick={{ fill: chartTheme.axisTick }}
                  axisLine={{ stroke: chartTheme.axisLine }}
                  tickLine={{ stroke: chartTheme.axisLine }}
                />
                <YAxis
                  tick={{ fill: chartTheme.axisTick }}
                  axisLine={{ stroke: chartTheme.axisLine }}
                  tickLine={{ stroke: chartTheme.axisLine }}
                />
                <Tooltip
                  cursor={{ stroke: chartTheme.cursorFill, strokeWidth: 2 }}
                  content={(tooltipProps) => <ActivityTooltip {...tooltipProps} styles={chartTooltipStyles} />}
                />
                <Legend content={({ payload }) => <ActivityLegend payload={payload as ReadonlyArray<ActivityLegendEntry> | undefined} />} />
                <Line type="monotone" dataKey="logins" stroke="#4a90e2" name="Logins" dot={renderDot('#4a90e2')} activeDot={{ r: 6, cursor: 'pointer' }} />
                <Line type="monotone" dataKey="downloads" stroke="#50e3c2" name="Downloads" dot={renderDot('#50e3c2')} activeDot={{ r: 6, cursor: 'pointer' }} />
                <Line type="monotone" dataKey="copies" stroke="#f5a623" name="Copies" dot={renderDot('#f5a623')} activeDot={{ r: 6, cursor: 'pointer' }} />
                <Line type="monotone" dataKey="uploads" stroke="#d0021b" name="Uploads" dot={renderDot('#d0021b')} activeDot={{ r: 6, cursor: 'pointer' }} />
                <Line type="monotone" dataKey="updates" stroke="#b388ff" name="Updates" dot={renderDot('#b388ff')} activeDot={{ r: 6, cursor: 'pointer' }} />
              </LineChart>
            </ResponsiveContainer>
            <p className="admin-dashboard__chart-hint">Click on a day to see detailed activity</p>
          </div>
        ) : (
          <div className="admin-dashboard__empty">No activity recorded for the selected window.</div>
        )}

        {selectedDay && (
          <div className="admin-dashboard__day-detail">
            <h3>Activity for {selectedDayLabel}</h3>
            {dayEventsLoading ? (
              <div className="admin-dashboard__empty">Loading events…</div>
            ) : dayEvents.length > 0 ? (
              <table className="admin-dashboard__table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event</th>
                    <th>User</th>
                    <th>Entry</th>
                    <th>Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {dayEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{formatTimestamp(event.createdAt)}</td>
                      <td>{formatEventType(event.type)}</td>
                      <td>{event.userName ?? '—'}</td>
                      <td>
                        {event.flowTitle ? (
                          <a href={`/flows/${event.flowId}`}>{event.flowTitle}</a>
                        ) : '—'}
                      </td>
                      <td>{formatMetadata(event.metadata)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="admin-dashboard__empty">No events recorded for this day.</div>
            )}
          </div>
        )}
      </section>
    );
  }, [activityHasSignal, chartTheme, chartTooltipStyles, data, handleChartClick, renderDot, selectedDay, selectedDayLabel, dayEventsLoading, dayEvents]);

  const onboardedUsers = useMemo<DashboardOnboardedUser[]>(() => {
    const all = data?.onboardedUsers ?? [];
    const q = onboardedSearch.trim().toLowerCase();
    if (!q) {
      return all;
    }
    return all.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        (u.email && u.email.toLowerCase().includes(q))
    );
  }, [data, onboardedSearch]);
  const totalOnboardedUsers = onboardedUsers.length;
  const totalOnboardedPages = totalOnboardedUsers > 0 ? Math.ceil(totalOnboardedUsers / ONBOARDED_USERS_PER_PAGE) : 1;
  useEffect(() => {
    setOnboardedPage(1);
  }, [onboardedSearch]);

  useEffect(() => {
    setOnboardedPage((prev) => {
      if (totalOnboardedUsers === 0) {
        return 1;
      }
      return Math.min(prev, totalOnboardedPages);
    });
  }, [totalOnboardedUsers, totalOnboardedPages]);

  const pagedOnboardedUsers = useMemo(() => {
    if (!totalOnboardedUsers) {
      return [];
    }
    const start = (onboardedPage - 1) * ONBOARDED_USERS_PER_PAGE;
    return onboardedUsers.slice(start, start + ONBOARDED_USERS_PER_PAGE);
  }, [onboardedUsers, onboardedPage, totalOnboardedUsers]);

  const showingRangeStart = totalOnboardedUsers ? (onboardedPage - 1) * ONBOARDED_USERS_PER_PAGE + 1 : 0;
  const showingRangeEnd = totalOnboardedUsers
    ? Math.min(onboardedPage * ONBOARDED_USERS_PER_PAGE, totalOnboardedUsers)
    : 0;

  const handlePrevOnboardedPage = useCallback(() => {
    setOnboardedPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNextOnboardedPage = useCallback(() => {
    setOnboardedPage((prev) => Math.min(totalOnboardedPages, prev + 1));
  }, [totalOnboardedPages]);

  const handleExportOnboardedUsers = useCallback(() => {
    if (!onboardedUsers.length) {
      return;
    }
    const headers = ['Name', 'Email', 'Role', 'Onboarded At', 'Last Active At'];
    const rows = onboardedUsers.map((user) => [
      user.name,
      user.email ?? '',
      formatRole(user.role),
      formatCsvTimestamp(user.onboardedAt),
      formatCsvTimestamp(user.lastActiveAt)
    ]);
    const csv = [headers, ...rows].map((row) => row.map(formatCsvValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = `onboarded-users-${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [onboardedUsers]);

  const visualOverviewData = useMemo(() => {
    if (!data) {
      return [];
    }
    const totals = data.totals;
    const groupConfig =
      VISUAL_OVERVIEW_GROUPS.find((group) => group.id === overviewGroup) || VISUAL_OVERVIEW_GROUPS[0];
    const definitionSet = overviewMode === 'recent' ? groupConfig.recentDefs : groupConfig.lifetimeDefs;
    return definitionSet.map((definition) => ({
      name: definition.label,
      value: safeValue(definition.getValue(totals))
    }));
  }, [data, overviewMode, overviewGroup]);

  return (
  <div className="admin-dashboard">
    <header className="admin-dashboard__header">
      <h1>Admin Dashboard</h1>
      <p className="admin-dashboard__subtitle">
        Updated {data ? new Date(data.generatedAt).toLocaleString() : '—'}
      </p>
    </header>

    {loading && <div className="admin-dashboard__loading">Loading dashboard…</div>}
    {!loading && error && <div className="admin-dashboard__error">{error}</div>}

    {!loading && !error && data && (
      <>
        {/* 📊 GRAPH SECTION */}
        <section className="admin-dashboard__section">
          <div className="admin-dashboard__section-header">
            <h2>Visual Overview</h2>
            <div className="admin-dashboard__section-tools admin-dashboard__section-tools--stacked">
              <div className="admin-dashboard__toggle-group" role="group" aria-label="Visual overview range">
                <button
                  type="button"
                  className={`admin-dashboard__toggle ${overviewMode === 'lifetime' ? 'is-active' : ''}`}
                  onClick={() => setOverviewMode('lifetime')}
                  aria-pressed={overviewMode === 'lifetime'}
                >
                  All Time
                </button>
                <button
                  type="button"
                  className={`admin-dashboard__toggle ${overviewMode === 'recent' ? 'is-active' : ''}`}
                  onClick={() => setOverviewMode('recent')}
                  aria-pressed={overviewMode === 'recent'}
                >
                  Last 7 Days
                </button>
              </div>
              <div
                className="admin-dashboard__toggle-group admin-dashboard__toggle-group--wide"
                role="group"
                aria-label="Visual overview category"
              >
                {VISUAL_OVERVIEW_GROUPS.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    className={`admin-dashboard__toggle ${overviewGroup === group.id ? 'is-active' : ''}`}
                    onClick={() => setOverviewGroup(group.id)}
                    aria-pressed={overviewGroup === group.id}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="admin-dashboard__charts">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={visualOverviewData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: chartTheme.axisTick }}
                  axisLine={{ stroke: chartTheme.axisLine }}
                  tickLine={{ stroke: chartTheme.axisLine }}
                />
                <YAxis
                  tick={{ fill: chartTheme.axisTick }}
                  axisLine={{ stroke: chartTheme.axisLine }}
                  tickLine={{ stroke: chartTheme.axisLine }}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  contentStyle={chartTooltipStyles.contentStyle}
                  labelStyle={chartTooltipStyles.labelStyle}
                  itemStyle={chartTooltipStyles.itemStyle}
                  filterNull={false}
                  isAnimationActive={false}
                  formatter={(value, _name, _entry, index) =>
                    index === 0 ? [value, 'value'] : [null]
                  }
                />
                <Bar
                  dataKey="value"
                  fill={chartTheme.barFill}
                  radius={[8, 8, 0, 0]}
                  isAnimationActive={!hoveredBar}
                  shape={
                    <VisualOverviewBar
                      fill={chartTheme.barFill}
                      activeName={hoveredBar}
                      onHover={handleBarHover}
                      onBlur={() => handleBarHover(null)}
                    />
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {activityChart}

        <section className="admin-dashboard__section">
          <div className="admin-dashboard__section-header">
            <h2>Onboarded Users</h2>
            <div className="admin-dashboard__section-tools">
              <input
                type="text"
                className="admin-dashboard__search-input"
                placeholder="Search by name or email..."
                value={onboardedSearch}
                onChange={(e) => setOnboardedSearch(e.target.value)}
              />
            {totalOnboardedUsers > 0 && (
              <>
                <p className="admin-dashboard__section-note">
                  Showing {formatNumber(showingRangeStart)}–{formatNumber(showingRangeEnd)} of{' '}
                  {formatNumber(totalOnboardedUsers)} users
                </p>
                {totalOnboardedPages > 1 && (
                  <div className="admin-dashboard__pagination">
                    <button
                      type="button"
                      className="admin-dashboard__button admin-dashboard__button--ghost"
                      onClick={handlePrevOnboardedPage}
                      disabled={onboardedPage === 1}
                    >
                      Prev
                    </button>
                    <span className="admin-dashboard__pagination-page">
                      Page {onboardedPage} / {totalOnboardedPages}
                    </span>
                    <button
                      type="button"
                      className="admin-dashboard__button admin-dashboard__button--ghost"
                      onClick={handleNextOnboardedPage}
                      disabled={onboardedPage === totalOnboardedPages}
                    >
                      Next
                    </button>
                  </div>
                )}
                <button type="button" className="admin-dashboard__button" onClick={handleExportOnboardedUsers}>
                  Export CSV
                </button>
              </>
            )}
            </div>
          </div>
          {pagedOnboardedUsers.length > 0 ? (
            <div className="admin-dashboard__user-list">
              {pagedOnboardedUsers.map((user) => (
                <div
                  className="admin-dashboard__user-pill admin-dashboard__clickable-row"
                  key={user.userId}
                  onClick={() => openUserDetail(user.userId)}
                >
                  <div className="admin-dashboard__user-pill-info">
                    <div className="admin-dashboard__user-avatar">{getInitials(user.name)}</div>
                    <div>
                      <div className="admin-dashboard__user-pill-name">{user.name}</div>
                      <div className="admin-dashboard__user-pill-email">{user.email ?? '—'}</div>
                    </div>
                  </div>
                  <div className="admin-dashboard__user-pill-meta">
                    <span className="admin-dashboard__chip">{formatRole(user.role)}</span>
                    <span>Joined {formatTimestamp(user.onboardedAt)}</span>
                    <span>Active {formatTimestamp(user.lastActiveAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="admin-dashboard__empty">No onboarded users yet.</div>
          )}
        </section>

        {/* 🔽 REMAINING SECTIONS */}
        <section className="admin-dashboard__split">
          <div className="admin-dashboard__section">
            <h2>Most Downloaded</h2>
            {data.topDownloadedFlows.length > 0 ? (
              <table className="admin-dashboard__table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Downloads</th>
                    <th>Last Download</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topDownloadedFlows.map((flow) => (
                    <tr key={flow.flowId}>
                      <td>{flow.title}</td>
                      <td>{formatNumber(flow.count)}</td>
                      <td>{formatTimestamp(flow.lastEventAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="admin-dashboard__empty">No download activity recorded yet.</div>
            )}
          </div>

          <div className="admin-dashboard__section">
            <h2>Most Copied</h2>
            {data.topCopiedFlows.length > 0 ? (
              <table className="admin-dashboard__table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Copies</th>
                    <th>Last Copy</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topCopiedFlows.map((flow) => (
                    <tr key={flow.flowId}>
                      <td>{flow.title}</td>
                      <td>{formatNumber(flow.count)}</td>
                      <td>{formatTimestamp(flow.lastEventAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="admin-dashboard__empty">No copy activity recorded yet.</div>
            )}
          </div>
        </section>

        <section className="admin-dashboard__section">
          <h2>Top Contributors</h2>
          {data.topUploaders.length > 0 ? (
            <table className="admin-dashboard__table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Uploads</th>
                  <th>Last Upload</th>
                </tr>
              </thead>
              <tbody>
                {data.topUploaders.map((user) => (
                  <tr key={user.userId}>
                    <td>{user.name}</td>
                    <td>{user.email ?? '—'}</td>
                    <td>{formatNumber(user.uploads)}</td>
                    <td>{formatTimestamp(user.lastUploadAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="admin-dashboard__empty">No uploads recorded yet.</div>
          )}
        </section>

        <section className="admin-dashboard__section">
          <div className="admin-dashboard__section-header">
            <h2>User Activity</h2>
            <div className="admin-dashboard__section-tools">
              <input
                type="text"
                className="admin-dashboard__search-input"
                placeholder="Search by name or email..."
                value={userActivitySearch}
                onChange={(e) => setUserActivitySearch(e.target.value)}
              />
            </div>
          </div>
          {filteredUserActivity.length > 0 ? (
            <div className="admin-dashboard__table-wrapper">
              <table className="admin-dashboard__table admin-dashboard__table--wide">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th>Last Download</th>
                    <th>Last Copy</th>
                    <th>Last Flow Upload</th>
                    <th>Last Forum Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUserActivity.map((userActivity) => (
                    <tr
                      key={userActivity.userId}
                      className="admin-dashboard__clickable-row"
                      onClick={() => openUserDetail(userActivity.userId)}
                    >
                      <td>{userActivity.name}</td>
                      <td>{userActivity.email ?? '—'}</td>
                      <td>{userActivity.role ?? '—'}</td>
                      <td>{formatTimestamp(userActivity.lastLoginAt)}</td>
                      <td>{formatTimestamp(userActivity.lastDownloadAt)}</td>
                      <td>{formatTimestamp(userActivity.lastCopyAt)}</td>
                      <td>{formatTimestamp(userActivity.lastFlowCreatedAt)}</td>
                      <td>{formatTimestamp(userActivity.lastForumActivityAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-dashboard__empty">
              {userActivitySearch.trim() ? 'No users match your search.' : 'No user activity recorded yet.'}
            </div>
          )}
        </section>

        <section className="admin-dashboard__section">
          <h2>Recent Activity</h2>
          {data.recentActivity.length > 0 ? (
            <table className="admin-dashboard__table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>User</th>
                  <th>Entry</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {data.recentActivity.map((event) => (
                  <tr key={event.id}>
                    <td>{formatTimestamp(event.createdAt)}</td>
                    <td>{formatEventType(event.type)}</td>
                    <td>{event.userName ?? '—'}</td>
                    <td>{event.flowTitle ?? '—'}</td>
                    <td>{formatMetadata(event.metadata)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="admin-dashboard__empty">No recent events recorded.</div>
          )}
        </section>

        <section className="admin-dashboard__section">
          <div className="admin-dashboard__section-header">
            <h2>Recently Deleted</h2>
            <div className="admin-dashboard__section-tools">
              <p className="admin-dashboard__section-note">
                Deleted entries are kept for 30 days before permanent removal.
              </p>
            </div>
          </div>
          {deletedLoading ? (
            <div className="admin-dashboard__empty">Loading deleted entries…</div>
          ) : deletedFlows.length > 0 ? (
            <table className="admin-dashboard__table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Kind</th>
                  <th>Deleted At</th>
                  <th>Deleted By</th>
                  <th>Days Left</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {deletedFlows.map((flow) => (
                  <tr key={flow.id}>
                    <td>{flow.title}</td>
                    <td>{flow.kind === 'node' ? 'Node' : 'Flow'}</td>
                    <td>{formatTimestamp(flow.deletedAt)}</td>
                    <td>{flow.deletedByName}</td>
                    <td>
                      <span className={flow.daysRemaining <= 5 ? 'admin-dashboard__days-warning' : ''}>
                        {flow.daysRemaining} day{flow.daysRemaining !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="admin-dashboard__button"
                        onClick={() => handleRestore(flow.id)}
                        disabled={restoringId === flow.id}
                      >
                        {restoringId === flow.id ? 'Restoring…' : 'Restore'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="admin-dashboard__empty">No deleted entries.</div>
          )}
        </section>

      </>
    )}

    {selectedUserId ? (
      <div className="admin-dashboard__modal">
        <div className="admin-dashboard__modal-backdrop" onClick={closeUserDetail} />
        <div className="admin-dashboard__modal-panel">
          <div className="admin-dashboard__modal-header">
            <div>
              {userDetail ? (
                <>
                  <h2>{userDetail.user.name}</h2>
                  <div className="admin-dashboard__modal-subtitle">
                    {userDetail.user.email ?? '—'}
                    {userDetail.user.role ? (
                      <span className="admin-dashboard__chip" style={{ marginLeft: '0.5rem' }}>
                        {formatRole(userDetail.user.role)}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : (
                <h2>User Activity</h2>
              )}
            </div>
            <button
              type="button"
              className="admin-dashboard__button admin-dashboard__button--ghost"
              onClick={closeUserDetail}
            >
              Close
            </button>
          </div>

          {userDetailLoading ? (
            <div className="admin-dashboard__empty">Loading activity...</div>
          ) : userDetail && userDetail.timeline.length > 0 ? (
            <div className="admin-dashboard__modal-body">
              <table className="admin-dashboard__table admin-dashboard__timeline-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {userDetail.timeline.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatTimestamp(entry.timestamp)}</td>
                      <td>{formatEventType(entry.type)}</td>
                      <td>
                        {entry.flowTitle ? (
                          <a href={`/flows/${entry.flowId}`}>{entry.flowTitle}</a>
                        ) : entry.detail ? (
                          <span>{entry.detail}</span>
                        ) : entry.metadata ? (
                          <span>{formatMetadata(entry.metadata)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : userDetail ? (
            <div className="admin-dashboard__empty">No activity recorded for this user.</div>
          ) : (
            <div className="admin-dashboard__empty">Failed to load activity.</div>
          )}
        </div>
      </div>
    ) : null}

    {/* Flow Health Dashboard */}
    {healthData && healthData.totals.summaries > 0 && (
      <div className="admin-dashboard__section">
        <h2>Flow Health</h2>
        <p className="admin-dashboard__section-desc">
          Aggregated validation results from extensions with health reporting enabled (last 30 days).
        </p>

        <div className="admin-dashboard__health-totals">
          <div className="admin-dashboard__health-stat">
            <span className="admin-dashboard__health-value">{healthData.totals.summaries}</span>
            <span className="admin-dashboard__health-label">validations</span>
          </div>
          <div className="admin-dashboard__health-stat">
            <span className="admin-dashboard__health-value">{healthData.totals.domains}</span>
            <span className="admin-dashboard__health-label">domains</span>
          </div>
          <div className="admin-dashboard__health-stat">
            <span className="admin-dashboard__health-value">{healthData.totals.uniqueFlows}</span>
            <span className="admin-dashboard__health-label">unique flows</span>
          </div>
        </div>

        {/* Issues Over Time */}
        {healthData.timeline.length > 0 && (
          <div className="admin-dashboard__chart-wrapper">
            <h3>Issues Over Time</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={healthData.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#30363d' : '#e0e0e0'} />
                <XAxis dataKey="date" tick={{ fill: theme === 'dark' ? '#8b949e' : '#666', fontSize: 11 }} />
                <YAxis tick={{ fill: theme === 'dark' ? '#8b949e' : '#666', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#161b22' : '#fff', border: `1px solid ${theme === 'dark' ? '#30363d' : '#ddd'}`, borderRadius: 6 }} />
                <Legend />
                <Line type="monotone" dataKey="errors" stroke="#f87171" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="warnings" stroke="#fbbf24" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Most Common Issues */}
        {healthData.topRules.length > 0 && (
          <div className="admin-dashboard__chart-wrapper">
            <h3>Most Common Validation Issues</h3>
            <ResponsiveContainer width="100%" height={Math.max(200, healthData.topRules.length * 32)}>
              <BarChart data={healthData.topRules} layout="vertical" margin={{ left: 150 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#30363d' : '#e0e0e0'} />
                <XAxis type="number" tick={{ fill: theme === 'dark' ? '#8b949e' : '#666', fontSize: 11 }} />
                <YAxis type="category" dataKey="ruleId" tick={{ fill: theme === 'dark' ? '#8b949e' : '#666', fontSize: 11 }} width={140} />
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#161b22' : '#fff', border: `1px solid ${theme === 'dark' ? '#30363d' : '#ddd'}`, borderRadius: 6 }} />
                <Bar dataKey="count" fill="#4A90D9" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-Domain Breakdown */}
        {healthData.byDomain.length > 0 && (
          <div className="admin-dashboard__chart-wrapper">
            <h3>Per-Domain Breakdown</h3>
            <table className="admin-dashboard__table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Flows</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {healthData.byDomain.map((d) => (
                  <tr key={d.domainId}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.domainId}</td>
                    <td>{d.flowCount}</td>
                    <td>{d.issueCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )}
  </div>
);
}
